import { parseMboxFromSplits, type IMboxMessageBounds } from '../gflib/src/lib/formats/mbox/parseMboxFromSplits';
import { assertGoodAndUnwrap } from '../gflib/src/lib/utils/result';
import type { RandomAccessFileReader } from '../gflib/src/lib/byte-reader/types';

/**
 * App-level wrapper around parseMboxFromSplits that provides
 * per-message bounds access and on-demand message reading with unescaping.
 */
export class AppMboxReader {
	public readonly messageSplits: IMboxMessageBounds[];
	private readonly _doUnescape: boolean;

	private constructor(
		private readonly _fileReader: RandomAccessFileReader,
		messageSplits: IMboxMessageBounds[],
		doUnescape: boolean
	) {
		this.messageSplits = messageSplits;
		this._doUnescape = doUnescape;
	}

	public static async create(fileReader: RandomAccessFileReader) {
		const result = assertGoodAndUnwrap(await parseMboxFromSplits(fileReader));
		return new AppMboxReader(
			fileReader,
			result.messageSplits,
			result.unescapeRecommendation === 'DO'
		);
	}

	public get numMessages() {
		return this.messageSplits.length;
	}

	public getMessageBounds(index: number): { start: number; endPlusOne: number } {
		const s = this.messageSplits[index];
		return { start: s.start, endPlusOne: s.endPlusOne };
	}

	public async readMessage(index: number): Promise<Buffer> {
		const split = this.messageSplits[index];
		if (!this._doUnescape || !split.offsetsOfEscapedFrom?.length) {
			return await this._fileReader.readBytes(
				split.start,
				split.endPlusOne - split.start
			);
		}

		// Read in pieces, skipping the escape byte before each ">From "
		const chunks: Buffer[] = [];
		let cursor = split.start;
		for (const escapedOffset of split.offsetsOfEscapedFrom) {
			const skipIndex = escapedOffset - 1;
			if (skipIndex > cursor) {
				chunks.push(await this._fileReader.readBytes(cursor, skipIndex - cursor));
			}
			cursor = escapedOffset;
		}
		if (cursor < split.endPlusOne) {
			chunks.push(await this._fileReader.readBytes(cursor, split.endPlusOne - cursor));
		}
		return Buffer.concat(
			// Little typescript hack so it won't yell about Buffer -> Uint8Array
			chunks as unknown as Uint8Array[],
			split.endPlusOne - split.start - split.offsetsOfEscapedFrom.length
		);
	}
}
